import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Clock, ClipboardList, Home, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OrderStatusTimeline } from "@/components/OrderStatusTimeline";
import { LoyaltyCard } from "@/components/LoyaltyCard";
import { useUI } from "@/context/UIContext";
import { useLang } from "@/context/LanguageContext";
import { useOrder } from "@/context/OrderContext";
import { isApiMenuSource } from "@/config/menuSource";

export default function ConfirmationPage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { order } = useOrder();
  const { t } = useLang();
  const { openStaffRating } = useUI();

  useEffect(() => {
    if (!order || order.id !== orderId) {
      const timer = setTimeout(() => navigate("/home"), 50);
      return () => clearTimeout(timer);
    }
  }, [order, orderId, navigate]);

  if (!order) return null;

  return (
    <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col bg-background px-6 pb-10 pt-safe" data-testid="confirmation-page">
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        {/* Animated success mark */}
        <div className="relative mb-6">
          <motion.span
            className="absolute inset-0 rounded-full bg-success/25"
            initial={{ scale: 0.6, opacity: 0.8 }}
            animate={{ scale: 2.2, opacity: 0 }}
            transition={{ duration: 1.1, repeat: 1 }}
          />
          <motion.div
            className="grid h-24 w-24 place-items-center rounded-full bg-success text-white shadow-float"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", damping: 12, stiffness: 220 }}
          >
            <motion.span
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
            >
              <Check className="h-12 w-12" strokeWidth={3} />
            </motion.span>
          </motion.div>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="font-heading text-2xl font-extrabold leading-tight"
        >
          {t("orderPlaced")}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="mt-2 max-w-xs text-sm text-muted-foreground"
        >
          {t("orderPlacedDesc")}
        </motion.p>

        {/* Order summary chips */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mt-6 grid w-full grid-cols-3 gap-3"
        >
          <SummaryChip label={t("orderNumber")} value={`#${order.number}`} />
          {order.tableNumber != null && <SummaryChip label={t("table")} value={order.tableNumber} />}
          {order.estReadyMin != null && (
            <SummaryChip label={t("estReady")} value={`~${order.estReadyMin}`} suffix={t("min")} icon={<Clock className="h-3.5 w-3.5" />} />
          )}
        </motion.div>

        {/* Status timeline */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mt-6 w-full rounded-3xl border border-border bg-card p-5 text-start shadow-soft"
        >
          <h3 className="mb-4 font-heading text-base font-bold">{t("orderStatus")}</h3>
          <OrderStatusTimeline status={order.status} />
        </motion.div>

        {/* Loyalty join / progress */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-5 w-full text-start">
          <LoyaltyCard />
        </motion.div>
      </div>

      <div className="mt-6 space-y-3">
        {/* Staff rating targets the demo staff; no real staff model yet. */}
        {!isApiMenuSource() && (
          <Button
            onClick={openStaffRating}
            variant="outline"
            className="h-12 w-full rounded-full text-base font-semibold"
            data-testid="rate-waiter-btn"
          >
            <Star className="me-2 h-4 w-4" /> {t("rateWaiter")}
          </Button>
        )}
        <Button
          onClick={() => navigate("/order")}
          className="h-14 w-full rounded-full brand-gradient text-base font-bold text-white shadow-soft"
          data-testid="track-order-btn"
        >
          <ClipboardList className="me-2 h-5 w-5" /> {t("trackOrder")}
        </Button>
        <Button
          onClick={() => navigate("/home")}
          variant="outline"
          className="h-12 w-full rounded-full text-base font-semibold"
          data-testid="back-home-btn"
        >
          <Home className="me-2 h-4 w-4" /> {t("backToHome")}
        </Button>
      </div>
    </div>
  );
}

function SummaryChip({ label, value, suffix, icon }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-3 text-center shadow-soft">
      <div className="mb-1 flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="font-heading text-lg font-extrabold leading-none">
        {value}
        {suffix && <span className="ms-0.5 text-xs font-bold text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}
